/**
 * Dice Admin — settings panel. Exposes the real-money toggle, bot bankroll,
 * and bot aggressiveness multiplier. This is the "make more house money"
 * knob: raise it to make bots post/fade more side bets; lower it to give
 * humans an easier ride.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bot, DollarSign, ShieldCheck, TrendingUp, Users } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Slider } from '../../../components/ui/slider';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt$ = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DiceSettingsPanel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [bankrollInput, setBankrollInput] = useState('');
    const [aggInput, setAggInput] = useState(1.0);
    const [hostAmt, setHostAmt] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/street-dice/settings`, { withCredentials: true });
            setData(data);
            setBankrollInput(String(data.bot_bankroll ?? 0));
            setAggInput(Number(data.bot_aggressiveness ?? 1.0));
            setHostAmt(String(data.host_reward_amount ?? 5));
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not load Dice settings');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (patch) => {
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/street-dice/settings`, patch, { withCredentials: true },
            );
            setData(data);
            toast.success('Saved');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Save failed');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="text-white/50 text-sm p-6">Loading Dice settings…</div>;
    }
    if (!data) return null;

    const aggLabel = aggInput <= 0.5 ? 'Sleepy' : aggInput <= 0.9 ? 'Calm'
        : aggInput <= 1.1 ? 'Baseline' : aggInput <= 1.5 ? 'Hungry' : 'Feral';

    return (
        <div className="space-y-4" data-testid="dice-settings-panel">
            {/* Real-wallet toggle */}
            <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                <div className="flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-[#FFD700] mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-bold text-white">Real-money rooms enabled</p>
                        <p className="text-xs text-white/60 mt-0.5">
                            When ON, players may create <b>mode: real</b> rooms that debit their BETohalics cash wallet. Off = practice-only.
                        </p>
                    </div>
                    <Switch
                        checked={!!data.real_wallet_enabled}
                        onCheckedChange={(v) => save({ real_wallet_enabled: v })}
                        disabled={busy}
                        data-testid="dice-settings-real-wallet-toggle"
                    />
                </div>
            </div>

            {/* Bot bankroll */}
            <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-[#39FF14]" />
                    <p className="text-sm font-bold text-white">House bot bankroll</p>
                </div>
                <p className="text-xs text-white/60 mb-3">
                    Shared pool bots draw from to fade wagers. Refills automatically on room create. Current: <b className="text-white">{fmt$(data.bot_bankroll)}</b>
                </p>
                <div className="flex gap-2">
                    <Input
                        type="number"
                        value={bankrollInput}
                        onChange={(e) => setBankrollInput(e.target.value)}
                        className="flex-1 bg-black/40 border-white/10 text-white"
                        placeholder="100000"
                        data-testid="dice-settings-bankroll-input"
                    />
                    <Button
                        onClick={() => {
                            const n = parseFloat(bankrollInput);
                            if (!Number.isFinite(n) || n < 0) return toast.error('Enter a positive number');
                            save({ bot_bankroll: n });
                        }}
                        disabled={busy}
                        className="font-black text-black bg-[#39FF14] hover:bg-[#39FF14]/80"
                        data-testid="dice-settings-bankroll-save"
                    >
                        Save
                    </Button>
                </div>
            </div>

            {/* Bot aggressiveness */}
            <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-[#FF3B30]" />
                    <p className="text-sm font-bold text-white">Bot aggressiveness</p>
                    <span
                        className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-black tracking-widest bg-white/5 border border-white/10 text-white/80"
                        data-testid="dice-settings-agg-label"
                    >
                        {aggLabel} · {aggInput.toFixed(2)}×
                    </span>
                </div>
                <p className="text-xs text-white/60 mb-3">
                    Multiplier on how often bots post & fade side bets. <b className="text-white">1.0×</b> is the baseline personality profile. Higher = more bot side action = more revenue routed to house P&amp;L (but louder-feeling tables). Lower = calmer, more human-driven flow.
                </p>
                <Slider
                    min={0} max={2} step={0.05}
                    value={[aggInput]}
                    onValueChange={(v) => setAggInput(v[0])}
                    className="my-3"
                    data-testid="dice-settings-agg-slider"
                />
                <div className="flex items-center justify-between text-[10px] text-white/40 font-mono mb-3">
                    <span>0.00× off</span>
                    <span>1.00× baseline</span>
                    <span>2.00× feral</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {[0.5, 1.0, 1.25, 1.5, 2.0].map((p) => (
                        <button
                            key={p}
                            onClick={() => setAggInput(p)}
                            className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-white/5 hover:bg-white/10 border border-white/10 text-white/80"
                            data-testid={`dice-settings-agg-preset-${String(p).replace('.', '_')}`}
                        >
                            {p.toFixed(2)}×
                        </button>
                    ))}
                    <div className="flex-1" />
                    <Button
                        onClick={() => save({ bot_aggressiveness: aggInput })}
                        disabled={busy || Math.abs(Number(data.bot_aggressiveness ?? 1.0) - aggInput) < 1e-9}
                        className="font-black text-black bg-[#FFD700] hover:bg-[#FFD700]/80"
                        data-testid="dice-settings-agg-save"
                    >
                        <Bot className="w-4 h-4 mr-1" /> Apply
                    </Button>
                </div>
            </div>

            {/* Host reward — creator gets $X when a new signup arrives via
                their invite link and plays their first Street Dice hand. */}
            <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                <div className="flex items-start gap-3 mb-2">
                    <Users className="w-5 h-5 text-[#22c55e] mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-bold text-white">Host referral reward</p>
                        <p className="text-xs text-white/60 mt-0.5">
                            When a brand-new signup joins a room via the creator&apos;s invite link and plays their first hand, the creator earns this amount. Turns private tables into a friend-referral engine.
                        </p>
                    </div>
                    <Switch
                        checked={!!data.host_reward_enabled}
                        onCheckedChange={(v) => save({ host_reward_enabled: v })}
                        disabled={busy}
                        data-testid="dice-settings-host-toggle"
                    />
                </div>
                <div className="flex gap-2 items-center">
                    <Label className="text-xs text-white/60 min-w-[70px]">Amount ($)</Label>
                    <Input
                        type="number"
                        value={hostAmt}
                        onChange={(e) => setHostAmt(e.target.value)}
                        placeholder="5.00"
                        min={0}
                        max={100}
                        step={1}
                        className="bg-black/40 border-white/10 text-white"
                        data-testid="dice-settings-host-amount-input"
                    />
                    <Button
                        onClick={() => {
                            const n = parseFloat(hostAmt);
                            if (!Number.isFinite(n) || n < 0 || n > 100) {
                                return toast.error('Amount must be $0 – $100');
                            }
                            save({ host_reward_amount: n });
                        }}
                        disabled={busy}
                        className="font-black text-black bg-[#22c55e] hover:bg-[#22c55e]/80"
                        data-testid="dice-settings-host-amount-save"
                    >
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default DiceSettingsPanel;
